/**
 * Marketing Fulfillment Data Service
 * Manages client-provided fulfillment data (logins, assets, business details) per category.
 * Stored in marketingFulfillmentData/{userId} with shape:
 * { website: {...}, social: {...}, newsletter: {...}, paidAds: {...}, reviews: {...}, updatedAt }
 */
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { getAuth } from 'firebase/auth';

const COLLECTION = 'marketingFulfillmentData';
const STORAGE_PREFIX = 'marketing-fulfillment';

const DEFAULT_CATEGORY_SHAPES = {
  website: {
    accessLoginsCompleted: false,
    wordpressAdmin: '',
    wordpressAdminSent: false,
    wordpressNotBuiltOn: false,
    wordpressNotBuiltOnPlatform: '',
    hostingAccess: '',
    hostingUsername: '',
    hostingPassword: '',
    hostingIsGodaddy: false,
    hostingAccessSent: false,
    imageVideoAssets: [],
    businessEmail: '',
    businessPhone: '',
    businessAddress: '',
    brandFonts: '',
    brandColors: '',
    additionalBrandInsights: '',
    logoFiles: [],
    servicesList: [],
    serviceAreas: [],
    hoursOfOperation: '',
    googleBusinessProfileAccess: '',
    googleBusinessProfileAccessSent: false,
    linkedInLogin: '',
    linkedInUsername: '',
    linkedInPassword: ''
  },
  social: {
    metaBusinessSuiteAddedAsAdmin: false,
    instagramUsername: '',
    instagramPassword: '',
    accessLoginsCompleted: false,
    photoVideoAssets: []
  },
  newsletter: {
    accessLoginsCompleted: false,
    activeCampaignAccountCreated: false,
    activeCampaignLink: '',
    accountAccess: '',
    accountAccessSent: false,
    photoVideoContent: []
  },
  paidAds: {
    accessLoginsCompleted: false,
    metaBusinessSuiteAccess: '',
    metaBusinessSuiteAccessSent: false,
    googleAdsAccess: '',
    googleAdsAccessSent: false,
    googleLSAAccess: '',
    googleLSAAccessSent: false,
    googleLSANotApplicable: false,
    photoVideoAssets: []
  },
  reviews: {
    googleBusinessProfileAccess: '',
    googleBusinessProfileAccessSent: false
  }
};

/**
 * Get full marketing fulfillment data for a user
 * @param {string} userId
 * @returns {Promise<Object>} Document data with default shape for missing categories
 */
export const getMarketingFulfillmentData = async (userId) => {
  if (!userId) throw new Error('User ID is required');
  const docRef = doc(db, COLLECTION, userId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) {
    return {
      website: { ...DEFAULT_CATEGORY_SHAPES.website },
      social: { ...DEFAULT_CATEGORY_SHAPES.social },
      newsletter: { ...DEFAULT_CATEGORY_SHAPES.newsletter },
      paidAds: { ...DEFAULT_CATEGORY_SHAPES.paidAds },
      reviews: { ...DEFAULT_CATEGORY_SHAPES.reviews },
      updatedAt: null
    };
  }
  const data = snap.data();
  return {
    website: { ...DEFAULT_CATEGORY_SHAPES.website, ...(data.website || {}) },
    social: { ...DEFAULT_CATEGORY_SHAPES.social, ...(data.social || {}) },
    newsletter: { ...DEFAULT_CATEGORY_SHAPES.newsletter, ...(data.newsletter || {}) },
    paidAds: { ...DEFAULT_CATEGORY_SHAPES.paidAds, ...(data.paidAds || {}) },
    reviews: { ...DEFAULT_CATEGORY_SHAPES.reviews, ...(data.reviews || {}) },
    updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() ?? data.updatedAt ?? null
  };
};

// Internal helper to ensure the authenticated user can access a given fulfillment owner (user or company)
const ensureFulfillmentAccess = async (ownerId) => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User must be authenticated to update fulfillment data');
  }

  // Direct match (per-user storage)
  if (auth.currentUser.uid === ownerId) {
    return;
  }

  // Company-level storage: ownerId may be the account/company id on the user document
  try {
    const userSnap = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const companyId = userData.accountId || userData.clientId || null;
      if (companyId && companyId === ownerId) {
        return;
      }
    }
  } catch (err) {
    console.error('Error checking fulfillment access:', err);
  }

  throw new Error('User can only update their own or their company fulfillment data');
};

/**
 * Update one category of fulfillment data (merge into existing)
 * @param {string} userId - Owner id (user uid or company/account id)
 * @param {'website'|'social'|'newsletter'|'paidAds'|'reviews'} category
 * @param {Object} data - Partial category object to merge
 */
export const updateMarketingFulfillmentCategory = async (userId, category, data) => {
  if (!userId) throw new Error('User ID is required');
  await ensureFulfillmentAccess(userId);

  const docRef = doc(db, COLLECTION, userId);
  const snap = await getDoc(docRef);
  const existing = snap.exists() ? snap.data() : {};
  const currentCategory = existing[category] || {};
  const merged = { ...currentCategory, ...data };
  await setDoc(
    docRef,
    {
      ...existing,
      [category]: merged,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );
  return merged;
};

/**
 * Upload a file to Storage and return its download URL
 * Path: marketing-fulfillment/{userId}/{category}/{subpath?}/{fileName}
 * @param {string} userId
 * @param {string} category
 * @param {File} file
 * @param {string} [subpath] - e.g. 'logos', 'assets'
 * @returns {Promise<string>} Download URL
 */
export const uploadFulfillmentFile = async (userId, category, file, subpath = '') => {
  if (!userId || !category || !file) throw new Error('userId, category, and file are required');
  await ensureFulfillmentAccess(userId);
  const safeName = `${Date.now()}-${(file.name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_')}`;
  const pathParts = [STORAGE_PREFIX, userId, category];
  if (subpath) pathParts.push(subpath);
  pathParts.push(safeName);
  const path = pathParts.join('/');
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

/**
 * Delete a file from Storage by its full path or URL
 * @param {string} pathOrUrl - Storage path (e.g. marketing-fulfillment/uid/website/logos/123.png) or download URL
 */
export const deleteFulfillmentFile = async (pathOrUrl) => {
  if (!pathOrUrl) return;
  let path = pathOrUrl;
  if (pathOrUrl.startsWith('http')) {
    try {
      const match = pathOrUrl.match(/\/o\/(.+?)\?/);
      path = match ? decodeURIComponent(match[1]) : pathOrUrl;
    } catch {
      return;
    }
  }
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
};

export { DEFAULT_CATEGORY_SHAPES };
